import {
  useQueryClient,
  type InfiniteData,
  type QueryKey,
} from "@tanstack/react-query";
import {
  notification_status,
  notification_event_type,
} from "prisma-generated/enums";
import React, { useRef, useEffect } from "react";
import { apiRequest } from "shared/api/apiRequest";
import { patchSocietyProfileNotificationsContract } from "shared/api/contracts/society/societyId/profiles/profileId/notifications";
import { type PaginatedResults } from "shared/api/types/pagination";
import BellIcon from "shared/components/icons/BellIcon";
import CrossIcon from "shared/components/icons/sign/CrossIcon";
import PillarForm from "shared/components/pillar-form/PillarForm";
import PillarModalBody from "shared/components/pillar-modal/NewPillarModalBody";
import { useCustomSession } from "shared/context/CustomSessionContext";
import type { NotificationEventView } from "shared/mappers/database/notification-event/notification-event";
import {
  NotificationsTable,
  getNotificationStatus,
} from "hub/src/components/common/modal/notifications/NotificationsTable";
import { useNotificationsContext } from "hub/src/context/NotificationsProvider/NotificationsContext";

export const NotificationsModalTrigger: React.FC = () => {
  const queryClient = useQueryClient();
  const session = useCustomSession();
  const { notificationsModalControl: control, unreadNotificationsCount } =
    useNotificationsContext();

  const patchAllUnreadPushNotificationStatuses = async () => {
    if (!session?.societyId || !session?.profileId) {
      return;
    }

    const notificationsQueryKey: QueryKey = [
      "pillarTable",
      "notifications",
      session.societyId.toString(),
      session.profileId.toString(),
    ];

    const queriesData = queryClient.getQueriesData<
      InfiniteData<PaginatedResults<NotificationEventView>>
    >({ queryKey: notificationsQueryKey });

    const allResults = queriesData
      .flatMap(([, queryValue]) => queryValue?.pages ?? [])
      .flatMap((page) => page?.results ?? []);

    const unreadPushNotificationIdsSet = new Set<number>();

    allResults.forEach((notification) => {
      const isPushNotification =
        notification.eventType === notification_event_type.push_notification;
      const isRead =
        getNotificationStatus(notification) === notification_status.read;

      if (isPushNotification && !isRead) {
        unreadPushNotificationIdsSet.add(notification.notificationEventId);
      }
    });

    const notificationEventIds = Array.from(
      unreadPushNotificationIdsSet.values(),
    );

    try {
      if (!notificationEventIds.length) {
        return;
      }

      return await apiRequest({
        contract: patchSocietyProfileNotificationsContract,
        inputs: {
          params: {
            societyId: session.societyId,
            profileId: session.profileId,
          },
          body: {
            notificationEventIds,
          },
        },
      });
    } finally {
      await queryClient.invalidateQueries({
        queryKey: [
          "notificationsUnreadCount",
          session.societyId.toString(),
          session.profileId.toString(),
        ],
      });
      await queryClient.invalidateQueries({ queryKey: notificationsQueryKey });
    }
  };

  const isOpenRef = useRef(control.isOpen);
  useEffect(() => {
    if (isOpenRef.current === control.isOpen) {
      return;
    }

    if (!control.isOpen) {
      // Mark notifications as read on the modal close
      patchAllUnreadPushNotificationStatuses();
    }

    isOpenRef.current = control.isOpen;
  }, [control.isOpen]);

  return (
    <>
      <PillarForm.Button
        id="notification-trigger"
        testid="notifications-menu-trigger"
        className="button-regular-general-unfilled relative size-6 rounded-full"
        onClick={() => {
          control.setIsOpen(true);
        }}
      >
        <BellIcon className="size-3 stroke-neutral-mid-600 group-hover:stroke-neutral-mid-900" />
        {!!unreadNotificationsCount && unreadNotificationsCount > 0 && (
          <div className="absolute top-1 right-1 size-1 rounded-full bg-status-terminated pointer-events-none" />
        )}
      </PillarForm.Button>

      <PillarModalBody
        control={control}
        className="notifications-modal"
        containerClasses="phone:overscroll-none"
        dialogPanelWrapperClasses="phone:max-h-full"
      >
        <div className="notifications-modal-header">
          <h3 className="appmodal-panel-title flex justify-center col-start-2">
            Notifications
          </h3>

          <PillarForm.Button
            className="button-regular-general-unfilled size-6 rounded-full col-start-3 absolute right-0 top-1/2 -translate-y-1/2"
            onClick={() => control.setIsOpen(false)}
          >
            <CrossIcon className="size-3" />
          </PillarForm.Button>
        </div>

        <NotificationsTable />
      </PillarModalBody>
    </>
  );
};
